import React from "react";

export function PageHeader({ overline, title, actions, sub }) {
  return (
    <div className="border-b border-white/10 px-8 py-6 flex items-end justify-between gap-4">
      <div>
        {overline && (
          <div className="text-[10px] tracking-[0.3em] uppercase text-zinc-500 mb-1.5">{overline}</div>
        )}
        <h1 className="font-heading text-3xl font-bold tracking-tighter uppercase">{title}</h1>
        {sub && <div className="text-xs text-zinc-500 mt-1.5">{sub}</div>}
      </div>
      {actions && <div className="flex items-center gap-2">{actions}</div>}
    </div>
  );
}

export function PrimaryButton({ children, ...rest }) {
  return (
    <button
      {...rest}
      className={`bg-[#FFB300] text-black font-bold uppercase tracking-widest text-[11px] px-4 py-2 hover:bg-[#FFC133] disabled:opacity-50 ${rest.className || ""}`}
    >
      {children}
    </button>
  );
}

export function GhostButton({ children, ...rest }) {
  return (
    <button
      {...rest}
      className={`border border-white/15 text-zinc-200 font-mono-data uppercase tracking-widest text-[11px] px-3 py-2 hover:bg-white/[0.05] hover:border-white/40 disabled:opacity-50 ${rest.className || ""}`}
    >
      {children}
    </button>
  );
}

export function Card({ children, className = "", ...rest }) {
  return (
    <div className={`bg-[#0A0A0A] border border-white/10 ${className}`} {...rest}>{children}</div>
  );
}

export function KPI({ label, value, hint, accent }) {
  return (
    <div className="bg-[#0A0A0A] border border-white/10 p-4 scanlines relative" data-testid={`kpi-${(label||"").toLowerCase().replace(/[^a-z0-9]+/g,'-')}`}>
      <div className="text-[10px] tracking-[0.25em] uppercase text-zinc-500">{label}</div>
      <div className={`font-heading font-bold mt-2 ${accent ? "text-[#FFB300]" : "text-white"} text-3xl tracking-tighter`}>
        {value ?? "—"}
      </div>
      {hint && <div className="text-[11px] text-zinc-500 mt-1.5 tracking-tight">{hint}</div>}
    </div>
  );
}

export function DataTable({ columns, rows, emptyText = "No records", onRowClick, testId, selectable = false, selectedIds, onToggleRow, onToggleAll }) {
  const sel = selectedIds || new Set();
  const allOnPage = rows.length > 0 && rows.every((r) => sel.has(r.id));
  return (
    <div className="border border-white/10 overflow-x-auto" data-testid={testId}>
      <table className="w-full text-xs">
        <thead className="bg-white/[0.02] border-b border-white/10">
          <tr>
            {selectable && (
              <th className="px-3 py-2 w-8">
                <input type="checkbox" checked={allOnPage} onChange={(e) => onToggleAll?.(e.target.checked)} data-testid="select-all-rows" aria-label="Select all" />
              </th>
            )}
            {columns.map((c) => (
              <th key={c.key} className="text-left px-3 py-2 font-mono-data text-[10px] uppercase tracking-widest text-zinc-500 whitespace-nowrap">
                {c.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 && (
            <tr><td colSpan={columns.length + (selectable ? 1 : 0)} className="px-3 py-8 text-center text-zinc-600 text-[11px] tracking-widest uppercase">{emptyText}</td></tr>
          )}
          {rows.map((r, i) => (
            <tr
              key={r.id || i}
              onClick={onRowClick ? () => onRowClick(r) : undefined}
              data-testid={`row-${r.id || i}`}
              className={`border-b border-white/5 hover:bg-white/[0.03] ${onRowClick ? "cursor-pointer" : ""} ${selectable && sel.has(r.id) ? "bg-[#FFB300]/[0.06]" : ""}`}
            >
              {selectable && (
                <td className="px-3 py-2.5 align-top w-8" onClick={(e) => e.stopPropagation()}>
                  <input type="checkbox" checked={sel.has(r.id)} onChange={() => onToggleRow?.(r.id)} data-testid={`select-row-${r.id}`} aria-label="Select row" />
                </td>
              )}
              {columns.map((c) => (
                <td key={c.key} className={`px-3 py-2.5 align-top tabular text-zinc-200 ${c.mono ? "font-mono-data text-[11px]" : ""}`}>
                  {c.render ? c.render(r) : (r[c.key] ?? <span className="text-zinc-700">—</span>)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function Field({ label, children, className = "" }) {
  return (
    <label className={`block ${className}`}>
      <div className="text-[10px] tracking-widest uppercase text-zinc-500 mb-1">{label}</div>
      {children}
    </label>
  );
}

export function Input(props) {
  return (
    <input
      {...props}
      className={`w-full bg-[#0A0A0A] border border-white/10 px-2.5 py-2 text-xs text-white focus:outline-none focus:border-[#FFB300] ${props.className || ""}`}
    />
  );
}

export function Textarea(props) {
  return (
    <textarea
      {...props}
      className={`w-full bg-[#0A0A0A] border border-white/10 px-2.5 py-2 text-xs text-white focus:outline-none focus:border-[#FFB300] ${props.className || ""}`}
    />
  );
}

export function Select({ children, ...rest }) {
  return (
    <select
      {...rest}
      className={`w-full bg-[#0A0A0A] border border-white/10 px-2.5 py-2 text-xs text-white focus:outline-none focus:border-[#FFB300] ${rest.className || ""}`}
    >
      {children}
    </select>
  );
}

export function DemoBadge({ show }) {
  if (!show) return null;
  return (
    <span className="ml-2 inline-flex items-center px-1.5 py-[1px] border border-zinc-700 text-[9px] tracking-widest uppercase text-zinc-500 font-mono-data">
      Demo
    </span>
  );
}
